import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import pendingApprovalsService, {
  EMPTY_COUNTS,
} from '@/services/approvals/pendingApprovalsService';
import { normalizeRole } from '@/utils/roleUtils';

const PendingApprovalsContext = createContext(null);

const POLL_INTERVAL_MS = 60_000;

const APPROVER_ROLES = new Set(['admin', 'manager', 'teamLeader', 'employee']);

export const usePendingApprovals = () => {
  const context = useContext(PendingApprovalsContext);
  if (!context) {
    throw new Error('usePendingApprovals must be used within PendingApprovalsProvider');
  }
  return context;
};

/** Safe hook — returns null when provider is missing (e.g. public pages) */
export const usePendingApprovalsOptional = () => {
  return useContext(PendingApprovalsContext);
};

export const PendingApprovalsProvider = ({ children }) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ ...EMPTY_COUNTS });
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  const canFetch = useMemo(() => {
    if (!user) return false;
    const role = normalizeRole(user.role);
    return APPROVER_ROLES.has(role);
  }, [user]);

  const refreshPendingCounts = useCallback(async ({ silent = false } = {}) => {
    if (!canFetch || fetchingRef.current) return;
    fetchingRef.current = true;
    if (!silent) {
      setLoading(true);
    }
    try {
      const data = await pendingApprovalsService.getPendingCounts();
      if (mountedRef.current) {
        setCounts(data);
      }
      return data;
    } catch (error) {
      console.warn('[PendingApprovals] Failed to fetch counts:', error?.message || error);
      return null;
    } finally {
      fetchingRef.current = false;
      if (mountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, [canFetch]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch when user becomes available
  useEffect(() => {
    if (!canFetch) {
      setCounts({ ...EMPTY_COUNTS });
      return;
    }
    refreshPendingCounts();
  }, [canFetch, refreshPendingCounts]);

  // Poll while tab is visible + refresh on focus
  useEffect(() => {
    if (!canFetch) return undefined;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        refreshPendingCounts({ silent: true });
      }
    };

    const intervalId = setInterval(tick, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshPendingCounts({ silent: true });
      }
    };

    const onFocus = () => refreshPendingCounts({ silent: true });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [canFetch, refreshPendingCounts]);

  const value = useMemo(
    () => ({
      counts,
      leaveRequests: counts.leaveRequests || 0,
      permissionRequests: counts.permissionRequests || 0,
      deviceRequests: counts.deviceRequests || 0,
      employeeRequests: counts.employeeRequests || 0,
      company: counts.company || 0,
      total: counts.total || 0,
      loading,
      refreshPendingCounts,
    }),
    [counts, loading, refreshPendingCounts]
  );

  return (
    <PendingApprovalsContext.Provider value={value}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};

export default PendingApprovalsContext;
